/* eslint-disable @typescript-eslint/naming-convention,sonarjs/no-duplicate-string */

import { createMachine } from 'xstate';
import type { StoreLocale, StoreFeatureFlags, Stores } from '@/root/constants';
import { FEATURE_FLAGS } from '@/root/constants';
import type { DiscountAllocation } from 'components/productContext/types/storeDiscountAllocation';
import type { StoreMoney } from 'components/productContext/types/storeMoney';
import type { CountryCode } from 'lib/shopify-storefront/__generated__/types';
import { CurrencyCode } from 'lib/shopify-storefront/__generated__/types';
import type { SignUpFormInput } from '../signUpForm/SignUpForm';

export type Maybe<T> = T | null;
export type InputMaybe<T> = Maybe<T>;

export type StoreAttribute = {
  key: string;
  value?: string | null;
};

// customerActivate, customerReset
export type StoreCartLines = {
  id: string;
  quantity: number;
  attributes: Array<StoreAttribute>;
  discountAllocations?: Array<DiscountAllocation>;
  merchandise: {
    id: string;
    availableForSale: boolean;
    requiresShipping: boolean;
    title: string;
    compareAtPrice: Maybe<StoreMoney>;
    price: {
      currencyCode: CurrencyCode;
      amount: number;
    };
    amountPerQuantity: {
      currencyCode: CurrencyCode;
      amount: number;
    };
    image: Maybe<{
      id: string;
      url: string;
      altText: string;
      width: number | null;
      height: number | null;
    }>;
    product: {
      handle: string;
      title: string;
      productType: string;
      tags: string[];
    };
    quantityAvailable: number | null;
    selectedOptions: Array<{
      name: string;
      value: string;
    }>;
    sku: string;
    metafields: Maybe<Record<string, string>>;
  };
};

export type StoreError = {
  code: string;
  message: string;
};

export interface StoreCustomer {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName?: string;
  pro: boolean;
  approvalPending: boolean;
  preferredLanguage: string | null;
}

export type StoreErrors = Maybe<Record<string, StoreError[]>>;

export type StoreCart = {
  note?: string | null;
  buyerIdentity: {
    countryCode: CountryCode | null;
    email: string | null;
    phone: string | null;
    customer: Maybe<StoreCustomer>;
  };
  checkoutUrl: string;
  lines: Array<StoreCartLines>;
  estimatedCost: {
    subtotalAmount: StoreMoney;
    totalAmount: StoreMoney;
    totalDutyAmount?: StoreMoney | null;
    totalTaxAmount?: StoreMoney | null;
  };
  attributes: Array<StoreAttribute>;
  discountCodes: Array<{
    code: string;
    applicable: boolean;
  }>;
  discountAllocations?: Array<DiscountAllocation>;
};

export const emptyCart: StoreCart = {
  buyerIdentity: {
    countryCode: null,
    email: null,
    phone: null,
    customer: null,
  },
  checkoutUrl: '',
  lines: [],
  estimatedCost: {
    subtotalAmount: {
      currencyCode: CurrencyCode['Usd'],
      amount: 0,
    },
    totalAmount: {
      currencyCode: CurrencyCode['Usd'],
      amount: 0,
    },
  },
  attributes: [],
  discountCodes: [],
};

export interface StoreContext {
  locale: StoreLocale;
  store: string;
  query: Record<string, unknown>;
  featureFlags: StoreFeatureFlags;
  cart: Maybe<Record<string, StoreCart>>;
  cartErrors: StoreErrors;
  cartId: Maybe<Record<string, string>>;
  checkoutId: Maybe<Record<string, string>>;
  customer: Maybe<Record<string, StoreCustomer>>;
  customerAccessToken: Maybe<Record<string, string>>;
  customerActivationToken: Maybe<string>;
  customerErrors: StoreErrors;
  customerId: Maybe<Record<string, string>>;
  customerResetToken: Maybe<string>;
  customerRestored: boolean;
}

export type StoreEvents =
  | { type: 'CART_REINIT' }
  | { type: 'CART_CREATE' }
  | { type: 'CART_ERROR'; payload: { errors: StoreError[] } }
  | { type: 'CART_FOUND' }
  | { type: 'CART_QUICK_ADD'; payload: { productId: string; quantity: number } }
  | { type: 'CART_LINE_ADD'; payload: { id: string; quantity: number }[] }
  | { type: 'CART_LINE_REMOVE'; payload: { ids: string | string[] } }
  | {
      type: 'CART_LINE_UPDATE';
      payload: { id: string; quantity: number };
    }
  | { type: 'CART_RECREATE'; payload?: { resetProCart?: boolean } }
  | { type: 'CART_STORAGE_EMPTY' }
  | {
      type: 'CART_ATTRIBUTES_UPDATED';
      payload: { cart: StoreCart; cartId: string };
    }
  | { type: 'CART_UPDATED'; payload: { cart: StoreCart; cartId: string } }
  | { type: 'CART_CREATED'; payload: { cart: StoreCart; cartId: string } }
  | {
      type: 'CART_GIFTS_CHECK';
      payload: { cart: StoreCart; cartId: string };
    }
  | {
      type: 'CART_DISCOUNT_CHECK';
      payload: { cart: StoreCart; cartId: string };
    }
  | {
      type: 'CART_DISCOUNT_CODES_UPDATE';
      payload: { id: string; discountCodes: string };
    }
  | {
      type: 'CUSTOMER_ACTIVATE';
      payload: { password: string; activationUrl: string; captcha: string };
    }
  | {
      type: 'CUSTOMER_CREATED';
      payload: {
        customerAccessToken: string;
        customer: StoreCustomer;
      };
    }
  | { type: 'CUSTOMER_CLEAR' }
  | { type: 'CUSTOMER_ERROR'; payload: { errors: StoreError[] } }
  | { type: 'CUSTOMER_ERROR_CLEAR' }
  | {
      type: 'CUSTOMER_FOUND';
      payload: {
        customer: StoreCustomer;
        cartId?: string;
        resetProCart?: boolean;
      };
    }
  | { type: 'CUSTOMER_IDLE' }
  | { type: 'CUSTOMER_RENEW' }
  | {
      type: 'CUSTOMER_RESET';
      payload: { password: string; resetUrl: string; captcha: string };
    }
  | {
      type: 'CUSTOMER_RECOVER';
      payload: { email: string; captcha: string };
    }
  | { type: 'CUSTOMER_RECOVER_SUCCESS' }
  | {
      type: 'CUSTOMER_RESTORE';
      payload: {
        customerAccessToken: string;
        customer?: StoreCustomer;
        customerActivationToken?: string;
        customerResetToken?: string;
      };
    }
  | {
      type: 'CUSTOMER_SIGN_IN';
      payload: { email: string; password: string };
    }
  | { type: 'CUSTOMER_SIGN_IN_ERROR'; payload: { errors: StoreError[] } }
  | { type: 'CUSTOMER_SIGN_OUT' }
  | { type: 'CUSTOMER_SIGNED_OUT' }
  | { type: 'CUSTOMER_SIGNED_OUT_ERROR' }
  | {
      type: 'CUSTOMER_SIGN_UP';
      payload: SignUpFormInput;
    }
  | { type: 'CUSTOMER_SIGN_IN_SUCCESS' }
  | {
      type: 'CUSTOMER_TOKEN';
      payload: { customerAccessToken: string; resetProCart?: boolean };
    }
  | { type: 'LOCALE_UPDATE'; payload: { locale: StoreLocale; store: Stores } };

export const storeModelInitial: StoreContext = {
  locale: 'en-US',
  store: 'default',
  query: {},
  featureFlags: FEATURE_FLAGS,
  cart: null,
  cartErrors: null,
  cartId: null,
  checkoutId: null,
  customer: null,
  customerAccessToken: null,
  customerActivationToken: null,
  customerErrors: null,
  customerId: null,
  customerResetToken: null,
  customerRestored: false,
};

export const storeMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5SwC4HsBOYCyBDAxgBYCWAdmAHT64Yp5FlgDEAwgKoDKAKgPLYCiAJQD6AMR5sAcgBEA2gAYAuolAAHNLGIpiaUipAAPRAGYAnGYoAOAKyWAjNbsAWAEzWXANmMB2ADQgAT0QXO3knK2NIp2MPD1C7SIBfRP9UTBwCEnIqGjpMxgoAMzAUBlIoJghdSlRcFBr0LHosympaZoLi0qyoBWUkEHVNbV19IwQPSxdjCg83F1MQ+Xlva1N-IIRXUwpvY0sPeTn5a1dLS2TUxoyy1tyO7K6yipYAQUEuYQAZAElJfmEbAACtJXlx+H19EMtDo9ANxtE9hQEqZJlM9qZ5JYNsFjO4KJiFuYPE5SctjJcQGkmvlsm08rciiVnjlaD8IGBSNoUAE2KoIHVmG8PsIhIIeIJIQNoSM4aBxh5VjM5u5FqEVmscQhTI5dmY7KY7DZjE5rN4XJTqTcWqyGTanj1bezOdzefzBax3p9gaDwXIlFCNDDRvDEHNJrsHGE7AlSSatQlLOFItYzcYXOadZYKSkqdcHnd2rTKA6yFBbQBFACuxHwAGtXhAIEwwBgMJgKKoADZ1QqYAC2FCtBdtI9L5UrNfrjYgCDIADc0NRZX0pWog7KxmHs8r5mrlqt1oFgpZFsj5EaEtZYqcnJb88XR4-x+X6dXaw2m56RWKJWvBhusJbhMSa7qqSwakemwLBYRrGGERzeIckwWrmw6PvSY7Mo6b5Tp+zbCp8vz-MIrzSP6-TrsMQGhggjjLFYqa2MsnguE4qxavBHg7PBzghJYJyON43j3ukI6Yc+2FlraXyMDOLZth23a9gOQ4PoyEmMi+MlyU2c6kIuy6wquAbSoBIbytuCSzHuEGHlqGZODsTjnOc0Q2DY1iiTSGn3JJ3TSfSsnkPJhGioI4qSqZVHBnKhjbtMNngeq9nHggGaohQZphD4rh2JM1g5lcYkYX5WlSROQW6QRXrCAA4j8ohcBwwgsAAEvwLAANL-jKNGWXR3jZhQyzZg4qLcacDneAxTg5ecyynJEqHFT5NqafaFWvrkdXEIUKCwG1uCkBAXbMK27YYJ2PYoH2GCDuhvlFuVAWVTte0HUdJ1nfphl1MZSi9eZcUKkhYELHZmppdMEZ7NM8j7HMTiKiJaHqetZWba9220Lt+2Hcdp1CrVv5RZRAHURZ8UTMJ4P7pBDlOA4ViJtep5TKYFxoyVT12p0W22njn2E2d37eiCYL8BRgaUyDYaeJYkbWNGsbRE4CaGnYWUI0m8jTD4MYeN51oFBt-PY7a0jELA+BoFWXIsGgHKwHyAr1Apl3XSp91qTzGPPVjLL0lbNt2w7TtwK7gq-Uu-26CZ5N9VToOFUlEMpVD0HplrHj0UhqwLNEXnc2tpuY+bQe5CHtv2ygjvO1H7thaTQOy8BirDSq6cHpnwSOFrJruJEOUOKjq0m3S5ePALwfWzX4cN+6Te1Q1TUte1nU9dFFOxcBZrDaNxjjbEOrq9Dc07Gx+XXmEQ0XneJcT4WfPTxbVXkIIYD9mg87nYpV3KVuqpR6-sX4lhnrkYKYBP7f1-jHIy8dAbbyTnLECCM0701StBGaLgKBzW8JzQq6ZLDeFJMbcSU9wFv0gYwGBP9iY-gin+ZBwN24ELppDKCwRkJWCQocQ0C1R7kNKgHCuOEaEfy-vQsW9VGrNVah1bqrdd60X3jMQ+x9Jpn2gqaHi8MXAuD1rEOwQ1hG8ywtQ2gUDG5-09oAu6D10Zl1Ea-SuVjGA2PgXHUgCcZYqIGoqVOXdMG93SiYnYOtnCrHNG4diZjQEWLcSgaxS8GGfBbiwtutFAkcIzlw9KBiPDIgNDNI4WIsRxMfhQlxVCkkpLdmk2Ra8FGb2UZuVRQ11FYiPmsE+U1oZ4h2PlPWmIjjsyNlUkRYCmQWygB9LgGBiBQBgBgV4+1WwsCrGkfsrYOAoDqFslghBjowA9kpG6Djfal0njUmZLI5n7QWUslZaz6gYE2ds3Z+yUCHOOeUMAXiVxIMTqw7JYMMGcK1AJIZrFwzmiQlzce1TpnaQeSgJ5yzWyvI2Vs9AOyMB7IObAI5JzGkZJBVkgJSoIV5KhdeCghihqjyxGsU88TnEooFmijFLz1nvNxWgfFhKfnEr+acsKq95EbyUZk-x1M1EjW6Zo0+Dl4IRKGosC+F5JrspuZyt+WBBQsFyGcgBFzgFOL1YknChr6jGtoICgGig2n9WppMdBwTIVpSHgSC8JwOauDMBMpFUzrWBVtWAe1KAZHkr8e0qlSJPW0rSt4ayawXIHHcAQ+QixdXPzDZVCNUaZEsEEPwSW0szKUvlZ0xVY1elaK1NecI7Fc7wVTAVUweanwvRZLgJsUbXgoBQIsgARlWeosBTVeyAT7EBHKC3ln7RAQdw6x0TrgI6xBzrZXxrdewmlPd8nZQJPYHUex2IGO7WbVxjpl2rpHcQcdk6Y1MLJnG11oNO62WTZsOIisjh4lCPsdtJxr2ULuXegduQh2PufXAGRYIuCCB+AAITYOCFqPoK0uuTvLWGUYmaq3jGlOwhjFaxKPvYXRyMu2TPMf5FkWBH1gF-sW5ub7cOoMVF4Q9DNSO3wZTNa8BDOY9OLiGhjvbHTMcWaxyNJqwrYb9Fxve6pGKpgEkYtiHFSPI0VuaLwAkZqGz2OB252lZPEHk+x2qZbS3lvBKp1R6nPLMW0+xawTbogMrCCSMjY09ZFTzH7BdjGZMlDk2xxTtVxBSErTFPd4x6LyA0+51inmEzZoJF4UebFogI2DSF65+bwvSS7GgF4tUvg8Dqs5gJCslYqxNGrLUE0RrsycnsVMwkH6SYSWVicFWoAuBkTVuqLh6s1oPkqhtKq0rZkVjfUwiwc0+AIXYcz+qmNgDIFoGR3AJSvDqgCfg2AgRcAAJpTYRGabwyJhOcxJE5PEWpXC4NiGYWwMFuJMy24uigWA9vRrCg5nDu7P2IFNMJB7JwnuknMF5tKxisrcVMDNc4Jikxj2K0-HtgcIvA5nA+p9G6p0XXOd7RxoWrWDfLED0gWhicweHaTydW6fHAo-XhkC1kk1Hre0hFmOoThuF1osHH87afSekgzpn0HaCwbZwhjjkUbsJVyQLtKURfPmngmU7Km36MDZlxOOXKBmeK9Z-BqdYUkMofQ5hwEEsVMQ55yltLWmMu6c2DGC8sxTRkZ60zTT-3HzECJmN2r6uthUeREG84GYzBYk4o4HYcQhoZhIexI+RWpelcZBH0WYVxuTbd6g6IRp4+okTxiFP2vaNZQNkaZYoykjG7C4XyPYUy1-B+FwGPlfFYojREnzE2JtfXnTytg0+xCqnFWGHrvxfaoVjYD8bqpFyKD8iMPhP6Jk8T82CaA0eCevuARheEhwX8-44KEXxpxEARkQSzvJLUO48j9r4fziJ8rBxEWFsBjHCT61x2RRHAfxkUlXXkUS3gpTlQRF32r1HzryPxMBiB4lIURhOASFTVANvxvUoEgJLz+ABDLWwB4AADUIRy9gJXAhpkDv9x9OJhJUs9ZkY7s0QYwb9LUC8bRiDqtSDndfQaD4D39Y8q8v8D9mDtcADdgBJUwxMYY8Ql9+Du9appAfgOAWAJBJBPgdDpB+AsMXdRDucK8s9GDpD69j9U0dhTBogFgscVsdQeCac+D79I8as3gvgARlNTCq0EDEAhoSQRpDhzQnJEIjg3t2ICQRdL0ZpiE6N+tO9+DGdtBcAuxrZGlDtBBjtTtzsrtB82I98a8rC0CEB9hMRUdrwuDW1XCSs79shgdiAMisiZE-DX8UE6D4ISiUCf9SNlZkQ3IADzALws1r0BV8VF1Khqg1JBQrk8d8BJjWxF0Y98okxT1lYDFThuIMxOIMxUsekCpYgQgyEO86RliMAAcCBtB5wPR2BDsBARBeAup+BJA1iyNUtrwmIRNDRTgXBVVmYzQmJPJSl8DeCqBLjrj8Bbj7jOBeAnjwo1daDaJ1jwhCE9ZYldi-BoZVRZhDQjjA10wJMwCMIoS6cqAi1LjWB4S+AhBWoy1wcxDIcEAYxDFqimJFQ-jXAEwiEsplZbBJg5hpgr1zjWhyTTdXwqTPkMAaTHj6TY0AjxC0TNjMSdiJdeSq9jNSFYhRNswVpSSNIJSCdpIHQPk8VWw5SET6S4sZAY8YhDgGVpgAthJlZs8Ewj5cEEZvsQgBTDh6jFjjSxFTTsJzTBVLSHjrSRBFTEsWT8oQiMTtjaM9jSN0cilTjGUuJSRRTkiLiZSAcsBYASgrS6TnieBXj3iUSBo2Svjc5OSNt-iHJZ9kR7DSRnAeskjDT1ogzb1Zc4BizIzSykTmFmSed8pSFVSkzsTGYVgKA1V-izgEYjdczxT8yKSsBbZf5ZTBzET7MqD6SOA2AWAWAjCOA1jFR0SbA1TkycTfd9NdhPB9gVgLx4zEUuzTYezakItNyIzaTESYy384zftJysSNTSM4hkx9QDEpgXJXAJi1zJTAc4BrgOAllSAfhSASzESXi3iPj2Tvj6zuSAToYcocsIZzQbBYx4KLSrj1zkL0hUKoB0LMKdyFTOMqzqZ4zLytjQKUys4YxdgmUTixpzRqLwzaLELNAmKMKsL6ScLKzRzUEayOSO0GyeTUyQgsp7B7Ar8zBDEDSCDPzINpIpLmLZKRAOAfg6pJBhA-hhz30lS4yZpuLrzpzSM9gilzQTgvAYkg8xKpiKTTKeAJ1zLhBLLrKpZhAJAB8OLxgHTUsDFPSSFVgsRSEEwjR09UwYhDY2ITR-KVjAq0LgqQc-yDyrL-hpAoqMN7LzyJzEzeLbyTA3S5z0dpgDh0d0cziVzISEL7RMAoA0Bh1OQgRcBYBYAAB3TAZsNYjyEaA0NwQ4RydiRq1kzHFstiQ4JcnUMjfKiStQ4vUqkQV4FgLgH4SgyWGPLAvBJcpaQxfTbREwZwgkGJFyTwP3KYXaiA7vQ64QMtDgfgGKxS4CPYGYOaHpBwvzGwBMFYe7OaHNccmwTEFQsUnqmir6g6+UkQPc6ghy2MnnalMGhwCG-TJHX3Exe7a8GIFyLA+fcEtw1G8S9GoUH68Kmyv4S6nzQm26yG0mxANk2GqYFwdEEkWwkkwy3qjwjGqMsK8q53S63jLm4mgqBMAxe7EkZWKJRKxwAM8SIy4gn60mVqHw94e0jgvBHTOIXfLERUByfhbWMIBYb0y8SXCEpYiWpotIlozIos6dexC1emt2tG8PT21oosjnXxRynnV63YXObPdtbglamIeCPBHwKYewWwEhMW12vWkO725mzG2y6QHwwfKYGO2wLAnA3AzifUhlewSYdHUIDWl2gOnOmEUO-O6Wv63gMtQfc0MuuOyunwByMIcIWw3PdMDKOmhowOxmx8VQUaiaqaz+Is6NGao+Oc8kc4dMCaconwQYyYZGJybahGaIT6x8UyyAGS1iiy2W6K88jY+q9Uvi4IJCVLZwYDFYbOf0s+xkC+iADCigeTB2QgMAesJgNY9HQ49HO+BGQebMTiExZUa-RbFbYEnWsk92moNCy+0gQB3+YB0BuscBuwIG1EyBlq4SLTKIWwYwfY+wp07McwBaOaGwH+m0P+gB2AKsfAfAOAW3Fm2Wuyw84808mPNiela-BwAxZ0qG7XRw56qYLEDag4dMNhgoDh3Bm44gX+UKstf4AAdTEdOFS01kvzmAmmiE4mWFSzroSDYiCwNDUeyA0YoC0Z0evt+qMIBtqpcqnLAuP32HuxsDrOymCbmCcawaYpwYoHQDrE5E-nIHGtCvkrEdNCKUkfcESqNF5oqJOBsc6rE3sCvAiaHGwf-twdific5DACSY8YAq6NoiFvMAZX7hP1cHcAevShsApuVlJAcM5lPAmVzFIAjngAGHzzMOAgAFo2ItQZmp7FjKFJnaJKitYPIHA+J3AfKtReMc1A0c1URlYwZVDgzyhlmBoHDrBLCx9rD0Crmp9hMPLeks6A6INtJ6RnQuQtA3QGlzm3U-UmtSQOn4JOn8o3A8F8pzBADya4KUbCDjK3paB3xpwmw-mEQOZdgEYCTlp2JOmj4kR0R+57DgSXnp63mIF3EQpUXI7UEF97tOqzR8oyN0wrH16hafBIgzAYK3ATnezEWUAhYCZvowA0Wwwr9dh7CwhACWViKdEFgrBKLW8M8FreWvzAoq454w465RmbFRW6JEQnT9QM0FhCoHJoVZhSkDgwXOYjhVWEWcZklaEpFf49XTgbA5zSELxgjlqcnHDR6zQSRiStNTg7X3mJEwBdWaW94zapgiEUqYxThfWj5whzhCpRcDYNrQ2uV5lFlMVVk+UwyhVvlflSVXWny9QTRhSSF8kEhQhkR3U1sR7Fss2DUwAjVchXWzbYggWlHPI7Am12TDEiazQ2IVt7AW2+0FcLc11lcxm8buMxjAXTRcqwhvMtYc81Yx3VhT64XyWLYrMbMO2o3aIXJ6UykDhlgmZURZXggjh0nhlFgTjkZ0GpMTShtKs9X-Srm3BOZkY3AjRkY2sfNrbt2YWKKJ3HRhsXBO2ZoY7rG4aHmoUgTgSD6NsuIIO+zgc9XEQdhqNc5CRDmPAdmYi-djhTxra-td2LMBZzdLdp3tAbdsPtKbIc0xdowVqUdHDmHIiTEMOJwuGeG+HP22Ysp7DphrxpG4heSGIjNk3qbsdSXFnbkH89WdTwhQhkrr8O4HBf8vTswpWc1sxA889XaIMH9AH-5IBVOhJzxNOPKmUcmaG5zuIuSLx7DimqPplzORn+B-5rOyNbPKH7P07oihl85sdBnq2X2TdUi26869WBI4hIxYgBIVGVgiP3L6Wph9cc1LbsuIm9XZm0oVtDiVhLw9nlsSmXxrOrnFb0weaExSQik0nKKTEsRTwFndbMH7XXGYTtHBQEv2STQBF0xlqDFVVXBfV9gEaRkSEquZ5pSaLBuikHB6JjNA3zBeSSEHz2rCpJWvB5uLYzTLi9WEhYhnPBn+EkY9YNYkvt3unxNErDudsV7BvcEYhU1smEimZaHoYDEviDEXIc1SEXrnvvz6EMBBvDi2r7AZpH3uIExdiWYu4XJx9hIwe+yrRGLmKEv0E6u7rlaSKNjU04g21Am5u4WjLtJTKMLBvFZ0xpvkYjQyeNYXJfVf3IGhbMmMf+OiqJ0oerABmDhzQTEdMHJm1ROQ8Tjc4d3uqZ6ArJLuHeGxrTuHSLuJpcv7Gb3WS1h7nOrya0w9Yqv+rBr6hSARqxrJqMAIBTvu2QKn6VqjQCFT0VQQDUwur3y8yg7l8RXj2BpRvcEkJOYEiNV3ToYokhN59WCEgjfKfuvzOKcsAbe-fqZt6ZhPBg+EZQ+0rz5uIcsNQQX7D9gSnvO0BfPLo9Xt7Usg+SEs-2Yc-oJphkxuI1s9LnsTOW74+iYkKfyCUlehOU+4qYIGVuJa-U7g-On9ZnIajemSET5ouPz4-c6sjVPtenyvj6vzA1UYhWG4-vebR57Lel7+yUBbeEyry-Hn6KjSFnJgi-N85MROuMH9-1GynafB++amUGV8p9cDQDwMvoIBJB8tMFRCppgeHfaekZRcZAM64IDesKdwxYhAvAywP-ul32Lutgmf7FvHDhKYuMBOyvOdoBR5zbEZgwkfDhBQ7hr8IYQmQxL9jbLMtcBb-TRn1xdYf90or9YpCtmiDmNAeCDPPvsH0qGJr25oRTl1xf7OMmBMTNAHE1IAJMamhXExKPVCApUDgCOHUFYxOAet+IpwCXD0WSDJAgAA */
  createMachine(
    {
      schema: {
        // The context (extended state) of the machine
        context: {} as StoreContext,
        events: {} as StoreEvents,
      },
      // eslint-disable-next-line @typescript-eslint/consistent-type-imports
      tsTypes: {} as import('./storeMachine.typegen').Typegen0,
      context: {
        locale: 'en-US',
        store: 'default',
        query: {},
        featureFlags: FEATURE_FLAGS,
        cart: null,
        cartErrors: null,
        cartId: null,
        checkoutId: null,
        customer: null,
        customerAccessToken: null,
        customerActivationToken: null,
        customerErrors: null,
        customerId: null,
        customerResetToken: null,
        customerRestored: false,
      },
      id: 'storeMachine',
      type: 'parallel',
      predictableActionArguments: true,
      states: {
        cartMachine: {
          initial: 'initialise',
          states: {
            fetching: {
              states: {
                cartIdentityUpdate: {
                  invoke: {
                    src: 'cartIdentityUpdate',
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_UPDATED: {
                      target: 'success',
                    },
                  },
                },
                cartQuickAdd: {
                  invoke: {
                    src: 'cartFindVariant',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_LINE_ADD: {
                      target: '#storeMachine.cartMachine.fetching.cartLineAdd',
                    },
                  },
                },
                cartLineAdd: {
                  invoke: {
                    src: 'cartLineAdd',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_GIFTS_CHECK: {
                      target:
                        '#storeMachine.cartMachine.fetching.cartGiftsHandle',
                    },
                  },
                },
                cartGiftsHandle: {
                  invoke: {
                    src: 'cartGiftsHandle',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_UPDATED: {
                      actions: ['assignCart', 'setCart'],
                      target: 'success',
                    },
                    CART_DISCOUNT_CHECK: {
                      target:
                        '#storeMachine.cartMachine.fetching.cartDiscountCheckHandle',
                    },
                  },
                },
                cartDiscountCodesUpdate: {
                  invoke: {
                    src: 'cartDiscountCodesUpdate',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_GIFTS_CHECK: {
                      target:
                        '#storeMachine.cartMachine.fetching.cartGiftsHandle',
                    },
                  },
                },
                cartDiscountCheckHandle: {
                  invoke: {
                    src: 'cartDiscountCheckHandle',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_UPDATED: {
                      actions: ['assignCart', 'setCart'],
                      target: 'success',
                    },
                    CART_DISCOUNT_CHECK: {
                      target:
                        '#storeMachine.cartMachine.fetching.cartDiscountCheckHandle',
                    },
                  },
                },
                cartLineRemove: {
                  invoke: {
                    src: 'cartLineRemove',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_GIFTS_CHECK: {
                      target:
                        '#storeMachine.cartMachine.fetching.cartGiftsHandle',
                    },
                  },
                },
                cartLineUpdate: {
                  invoke: {
                    src: 'cartLineUpdate',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_GIFTS_CHECK: {
                      target:
                        '#storeMachine.cartMachine.fetching.cartGiftsHandle',
                    },
                  },
                },
                giftTriggerAfterCustomerStatusChange: {
                  invoke: {
                    src: 'giftTriggerAfterCustomerStatusChange',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_GIFTS_CHECK: {
                      target:
                        '#storeMachine.cartMachine.fetching.cartGiftsHandle',
                    },
                  },
                },
                createCart: {
                  invoke: {
                    src: 'cartCreate',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_CREATED: {
                      target:
                        '#storeMachine.cartMachine.fetching.addCartAttributes',
                    },
                  },
                },
                addCartAttributes: {
                  invoke: {
                    src: 'addCartAttributes',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_ATTRIBUTES_UPDATED: {
                      actions: [
                        'assignCartId',
                        'setCartId',
                        'assignCart',
                        'setCart',
                      ],
                      target: 'success',
                    },
                  },
                },
                retrieveCart: {
                  invoke: {
                    src: 'cartById',
                  },
                  on: {
                    CART_ERROR: {
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_UPDATED: {
                      actions: ['assignCart', 'setCart'],
                      target: 'success',
                    },
                    CART_RECREATE: {
                      target: 'createCart',
                    },
                    CART_FOUND: {
                      target: 'success',
                    },
                  },
                },
                reinit: {
                  invoke: {
                    src: 'cartReinit',
                  },
                  on: {
                    CART_STORAGE_EMPTY: {
                      target: '#storeMachine.cartMachine.fetching.createCart',
                    },
                    CART_CREATED: {
                      target: [
                        '#storeMachine.cartMachine.fetching.reinitAddCartAttibutes',
                        // '#storeMachine.cartMachine.fetching.giftTriggerAfterCustomerStatusChange',
                        // '#storeMachine.cartMachine.fetching.log',
                      ],
                    },
                  },
                },
                reinitAddCartAttibutes: {
                  invoke: {
                    src: 'addCartAttributes',
                    onError: [
                      {
                        target: '#storeMachine.cartMachine.idle.errored',
                      },
                    ],
                  },
                  on: {
                    CART_ERROR: {
                      actions: 'assignCartErrors',
                      target: '#storeMachine.cartMachine.idle.errored',
                    },
                    CART_ATTRIBUTES_UPDATED: {
                      actions: [
                        'assignCartId',
                        'setCartId',
                        'assignCart',
                        'setCart',
                      ],
                      target:
                        '#storeMachine.cartMachine.fetching.giftTriggerAfterCustomerStatusChange',
                    },
                  },
                },
                success: {
                  type: 'final',
                },
              },
              on: {
                CART_LINE_UPDATE: {
                  target: '.cartLineUpdate',
                  internal: false,
                },
              },
              onDone: {
                target: 'idle',
              },
            },
            idle: {
              initial: 'noError',
              states: {
                errored: {},
                noError: {
                  entry: ['clearErrorMessage', 'updateLocalCart'],
                },
              },
              on: {
                CART_REINIT: {
                  target: '#storeMachine.cartMachine.fetching.reinit',
                },

                CART_QUICK_ADD: {
                  target: '#storeMachine.cartMachine.fetching.cartQuickAdd',
                },
                CART_LINE_ADD: {
                  target: '#storeMachine.cartMachine.fetching.cartLineAdd',
                },
                CART_GIFTS_CHECK: {
                  target: '#storeMachine.cartMachine.fetching.cartGiftsHandle',
                },
                CART_DISCOUNT_CHECK: {
                  target:
                    '#storeMachine.cartMachine.fetching.cartDiscountCheckHandle',
                },
                CART_LINE_REMOVE: {
                  target: '#storeMachine.cartMachine.fetching.cartLineRemove',
                },
                CART_LINE_UPDATE: {
                  target: '#storeMachine.cartMachine.fetching.cartLineUpdate',
                },
                CART_DISCOUNT_CODES_UPDATE: {
                  target:
                    '#storeMachine.cartMachine.fetching.cartDiscountCodesUpdate',
                },
                LOCALE_UPDATE: {
                  actions: ['assignCartLocale'],
                  target: [
                    '#storeMachine.cartMachine.initialise',
                    '#storeMachine.customerMachine.initialise',
                  ],
                },
              },
            },
            initialise: {
              description:
                'Initialise cart machine by calling cartInit service',
              invoke: {
                src: 'cartInit',
              },
              on: {
                CART_STORAGE_EMPTY: {
                  target: '#storeMachine.cartMachine.fetching.createCart',
                },
                CART_UPDATED: {
                  actions: ['assignCartId', 'assignCart'],
                  target: '#storeMachine.cartMachine.fetching.retrieveCart',
                },
                CART_REINIT: {
                  target: '#storeMachine.cartMachine.fetching.reinit',
                },
              },
            },
          },
          on: {
            CUSTOMER_FOUND: [
              {
                cond: 'cartIdFound',
                target: '.fetching.retrieveCart',
                internal: false,
              },
            ],
          },
        },
        customerMachine: {
          initial: 'initialise',
          states: {
            fetching: {
              states: {
                activate: {
                  invoke: {
                    src: 'customerActivate',
                  },
                  on: {
                    CUSTOMER_TOKEN: {
                      actions: ['assignCustomerToken', 'setCustomerToken'],
                      target: 'fetchCustomer',
                    },
                    CUSTOMER_ERROR: {
                      actions: 'assignCustomerError',
                      target: '#storeMachine.customerMachine.idle.errored',
                    },
                  },
                },
                createCustomer: {
                  invoke: {
                    src: 'customerCreate',
                  },
                  on: {
                    CUSTOMER_CREATED: {
                      actions: [
                        'assignCustomer',
                        'setCustomer',
                        'assignCustomerToken',
                        'setCustomerToken',
                      ],
                      target: 'success',
                    },
                    CUSTOMER_ERROR: {
                      actions: 'assignCustomerError',
                      target: '#storeMachine.customerMachine.idle.errored',
                    },
                  },
                },
                fetchCustomer: {
                  invoke: {
                    src: 'customerFetch',
                  },
                  on: {
                    CUSTOMER_FOUND: {
                      actions: ['assignCustomer', 'setCustomer'],
                      target: [
                        // '#storeMachine.cartMachine.fetching.giftTriggerAfterCustomerStatusChange',
                        'success',
                      ],
                    },
                    CUSTOMER_ERROR: {
                      actions: 'assignCustomerError',
                      target: '#storeMachine.customerMachine.idle.errored',
                    },
                  },
                },
                fetchNewCustomer: {
                  invoke: {
                    src: 'customerFetch',
                  },
                  on: {
                    CUSTOMER_FOUND: {
                      actions: ['assignCustomer', 'setCustomer'],
                      target: [
                        '#storeMachine.cartMachine.fetching.reinit',
                        'success',
                      ],
                    },
                    CUSTOMER_ERROR: {
                      actions: 'assignCustomerError',
                      target: '#storeMachine.customerMachine.idle.errored',
                    },
                  },
                },
                reset: {
                  invoke: {
                    src: 'customerReset',
                  },
                  on: {
                    CUSTOMER_TOKEN: {
                      actions: ['assignCustomerToken', 'setCustomerToken'],
                      target: 'fetchCustomer',
                    },
                    CUSTOMER_ERROR: {
                      actions: ['assignCustomerError', 'assignCustomerBlank'],
                      target: '#storeMachine.customerMachine.idle.errored',
                    },
                  },
                },
                recover: {
                  invoke: {
                    src: 'customerRecover',
                  },
                  on: {
                    CUSTOMER_RECOVER_SUCCESS: {
                      target:
                        '#storeMachine.customerMachine.idle.recoverSuccess',
                    },
                    CUSTOMER_ERROR: {
                      actions: ['assignCustomerError', 'assignCustomerBlank'],
                      target: '#storeMachine.customerMachine.idle.errored',
                    },
                  },
                },
                restoreSignIn: {
                  invoke: {
                    src: 'customerTokenRenew',
                  },
                  on: {
                    CUSTOMER_TOKEN: {
                      actions: [
                        'assignCustomerToken',
                        'setCustomerToken',
                        'assignCustomerRestored',
                      ],
                      target: 'fetchCustomer',
                    },
                    CUSTOMER_ERROR: {
                      actions: 'assignCustomerError',
                      target: '#storeMachine.customerMachine.idle.errored',
                    },
                  },
                },
                signIn: {
                  invoke: {
                    src: 'customerSignIn',
                  },
                  on: {
                    CUSTOMER_TOKEN: {
                      actions: ['assignCustomerToken', 'setCustomerToken'],
                      target: ['fetchNewCustomer'],
                    },
                    CUSTOMER_SIGN_IN_ERROR: {
                      actions: 'assignCustomerError',
                      target: '#storeMachine.customerMachine.idle.errored',
                    },
                  },
                },
                signOut: {
                  invoke: {
                    src: 'customerSignOut',
                  },
                  on: {
                    CUSTOMER_SIGNED_OUT: {
                      actions: ['assignCustomerBlank', 'setCustomerClear'],
                      target: [
                        // '#storeMachine.cartMachine.fetching.giftTriggerAfterCustomerStatusChange',
                        '#storeMachine.cartMachine.fetching.reinit',
                        '#storeMachine.customerMachine.idle.noError',
                      ],
                    },
                    CUSTOMER_SIGNED_OUT_ERROR:
                      '#storeMachine.customerMachine.idle.errored',
                  },
                },
                success: {
                  type: 'final',
                },
              },
              onDone: {
                target: 'signedIn',
              },
            },

            forgottenPassword: {
              always: {
                target: 'idle',
              },
            },

            idle: {
              initial: 'noError',
              states: {
                errored: {},

                noError: {
                  entry: 'clearErrorMessage',
                },

                recoverSuccess: {
                  entry: 'clearErrorMessage',
                },
              },
              on: {
                CUSTOMER_ACTIVATE: {
                  target: '#storeMachine.customerMachine.fetching.activate',
                },
                CUSTOMER_RESET: {
                  target: '#storeMachine.customerMachine.fetching.reset',
                },
                CUSTOMER_RECOVER: {
                  target: '#storeMachine.customerMachine.fetching.recover',
                },
                CUSTOMER_SIGN_IN: {
                  target: '#storeMachine.customerMachine.fetching.signIn',
                },
                CUSTOMER_SIGN_UP: {
                  target:
                    '#storeMachine.customerMachine.fetching.createCustomer',
                },
                CUSTOMER_ERROR_CLEAR: {
                  target: 'idle',
                },
              },
            },

            initialise: {
              description:
                'Initialise customer machine by calling customerInit service',
              invoke: {
                src: 'customerInit',
                onError: [
                  {
                    target: '#storeMachine.customerMachine.idle.errored',
                  },
                ],
              },
              on: {
                CUSTOMER_IDLE: {
                  actions: [
                    'assignCustomerActivationToken',
                    'assignCustomerResetToken',
                  ],
                  target: 'idle',
                },
                CUSTOMER_RESTORE: {
                  actions: [
                    'assignCustomerActivationToken',
                    'assignCustomerResetToken',
                    'assignCustomerToken',
                    'assignCustomer',
                  ],
                  target:
                    '#storeMachine.customerMachine.fetching.restoreSignIn',
                },
              },
            },

            passwordReset: {
              always: {
                target: 'idle',
              },
            },

            signedIn: {
              initial: 'eventCheck',
              states: {
                eventCheck: {
                  always: [
                    {
                      cond: 'restoredCustomer',
                      target: 'active',
                    },
                    {
                      target: 'success',
                    },
                  ],
                },
                success: {
                  on: {
                    CUSTOMER_SIGN_IN_SUCCESS: {
                      target: [
                        'active',
                        '#storeMachine.cartMachine.fetching.reinit',
                      ],
                    },
                  },
                },
                active: {
                  // after: {
                  //   5000: {
                  //     target: '#storeMachine.cartMachine.fetching.reinit',
                  //   },
                  // },
                  on: {
                    CUSTOMER_RENEW: {
                      target: 'tokenRenew',
                    },
                    CUSTOMER_RESET: {
                      target: '#storeMachine.customerMachine.fetching.reset',
                    },
                  },
                },
                tokenRenew: {
                  invoke: {
                    src: 'customerTokenRenew',
                  },
                  on: {
                    CUSTOMER_TOKEN: {
                      target: 'active',
                    },
                    CUSTOMER_ERROR: {
                      actions: 'assignCustomerError',
                      target: '#storeMachine.customerMachine.idle.errored',
                    },
                  },
                },
              },
              on: {
                CUSTOMER_SIGN_OUT: {
                  target: '#storeMachine.customerMachine.fetching.signOut',
                },
              },
            },
          },
        },
      },
    },
    {
      guards: {
        restoredCustomer: (context) => {
          return context.customerRestored;
        },
        cartIdFound: (_, event) => {
          console.log(event.type);
          if (event.type !== 'CUSTOMER_FOUND') return false;
          return !!(event.payload?.cartId ?? false);
        },
      },
    }
  );
